'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { SquarePlay } from 'lucide-react';
import { useQuery, useI18n, Button, toast, useMutation, useInterval, usePersistedCooldown, useQueryState } from '@ywwwtseng/react-kit';
import { Typography } from '@ywwwtseng/tma-react-kit';
import { useAd } from '@/lib/ad/client';
import * as web3 from '@ywwwtseng/web3';
import type { AdProviderId } from '@/lib/ad';
import type { SelectUser, SelectAdActivity } from '@/types';
import { AD_DAILY_LIMIT, MOONINI_DECIMALS } from '@/constants';

export function WatchAdReward() {
  const { t } = useI18n();
  const me = useQueryState<SelectUser>('me');
  const { open, status } = useAd();
  const [pollingActivityId, setPollingActivityId] = useState<string | null>(null);
  const createAdActivity = useMutation('ad_activity:create');
  const { isCoolingDown, remainingSeconds, startCooldown } = usePersistedCooldown(
    'ad_reward',
    60
  );

  const { data: adStatus, refetch: refetchAdStatus } = useQuery<{
    watched: number;
    remaining: number;
  }>('ad_status', {
    refetchOnMount: true,
  });

  const { data: activityStatus, refetch: refetchStatus } = useQuery<SelectAdActivity>('ad_activity', {
    params: { activity_id: pollingActivityId ?? '' },
    enabled: !!pollingActivityId,
  });

  useInterval(refetchStatus, {
    delay: 5000,
    timeout: 60_000,
    enabled: !!pollingActivityId,
    onTimeout: () => {
      toast.error(t('message.ad_reward_timeout'));
      setPollingActivityId(null);
    },
  });

  // 狀態變為 claimed：顯示成功、刷新限額、停止輪詢、開始 30 秒冷卻（重新整理後仍會倒數）
  useEffect(() => {
    if (activityStatus?.status === 'claimed') {
      toast.success(t('message.ad_reward_claimed_success', {
        amount: web3.utils.formatUnits(activityStatus.amount, MOONINI_DECIMALS)
      }));
      void refetchAdStatus();
      startCooldown();
      setPollingActivityId(null);
    }
  }, [activityStatus, startCooldown]);

  const openAd = async () => {
    if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') {
      toast.error('Ad reward is not available in this environment');
      return;
    }
    if (!adStatus) {
      return;
    }
    if (adStatus.remaining <= 0) {
      toast.error(t('message.daily_ad_limit_reached', {
        limit: String(AD_DAILY_LIMIT),
      }));
      return;
    }
    if (status !== 'ready') {
      toast.error(t('message.ad_sdk_not_ready'));
      return;
    }

    const res = await createAdActivity.mutate();
    const data = (res as { data: { id: string; provider_id: AdProviderId } })?.data;
    await open(data.provider_id);
    setPollingActivityId(data.id);
  };

  if (!me) return null;

  const remaining = adStatus?.remaining ?? AD_DAILY_LIMIT;
  const isExhausted = adStatus && adStatus.remaining <= 0;
  const isPolling = !!pollingActivityId;
  const disabledByCooldown = isCoolingDown;

  const getButtonLabel = () => {
    if (isPolling) return t('common.confirming');
    if (disabledByCooldown) return t('earn.watch_ad_cooldown_seconds', { seconds: String(remainingSeconds) });
    if (isExhausted) return t('earn.watch_ad_mission_exhausted');
    if (status !== 'ready') return t('common.loading');
    return '0.1 $MOONINI';
  };

  return (
    <div className="card flex flex-col w-full gap-3 p-2">
      <div className="flex items-center gap-2">
        <SquarePlay size={24} color="#ef4444" />
        <div className="flex flex-col flex-1 gap-1">
          <Typography size="2" weight={700} i18n="earn.watch_ad_mission" />
          {adStatus && (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <Typography size="1" className="opacity-80">
                  {t('earn.watch_ad_mission_remaining', {
                    remaining: String(remaining),
                    limit: String(AD_DAILY_LIMIT),
                  })}
                </Typography>
              </div>
            </div>
          )}
        </div>

        <div className="relative">
          <Button
            onClick={openAd}
            disabled={
              isExhausted
              || status !== 'ready'
              || createAdActivity.isLoading
              || isPolling
              || disabledByCooldown
            }
            variant="contained"
            rounded="full"
            size="xs"
            color="destructive"
          >
            {getButtonLabel()}
          </Button>
          <Image
            src="/images/cursor.gif"
            alt="cursor"
            width={40}
            height={40}
            className="absolute top-[18px] right-0 left-0 mx-auto"
          />
        </div>

      </div>
    </div>
  );
}
